import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import AccuracyGauge from "../components/AccuracyGauge";
import CalculatorForm from "../components/CalculatorForm";
import ResultsPanel from "../components/ResultsPanel";
import ComingSoonSection from "../components/ComingSoonSection";
import FireLogo from "../components/FireLogo";
import Footer from "../components/Footer";
import {
  calculateBmr,
  calculateConfidence,
  calculateLeanMassKg,
  calculateStepsAdjustment,
  calculateTdee,
  convertCmToFeetInches,
  convertHeightToCm,
  convertKgToUnit,
  convertWeightToKg,
  deriveTrainingProfile,
  getBaseActivityMultiplier,
  getCalculatorNetActivity,
  getTrainingModifiers,
  parseNumber,
} from "../utils/calculations";

const ageRange = { min: 13, max: 90 };
const heightRange = { min: 120, max: 230 };
const weightRange = { min: 35, max: 200 };
const bodyFatRange = { min: 3, max: 60 };
const STORAGE_KEY = "betterBmrCalculatorState";
const trainingLabels = {
  light: "Light",
  moderate: "Moderate",
  hard: "Hard",
  strength: "Strength",
  endurance: "Endurance",
  mixed: "Mixed",
};

const defaultBasics = {
  sex: "",
  age: "",
  heightUnit: "cm",
  heightCm: "",
  heightFt: "",
  heightIn: "",
  weightUnit: "kg",
  weight: "",
  bodyFatPct: "",
};

const defaultLifestyle = {
  trainingFrequency: "",
  trainingIntensity: "",
  trainingModality: "",
  jobActivity: "",
  sleepQuality: "",
  dietHistory: "",
  goal: "",
  dailySteps: "",
};

const createId = (prefix) =>
  `${prefix}-${Date.now()}-${Math.random()
    .toString(16)
    .slice(2)}`;

const formatSessionCount = (value) => {
  if (!Number.isFinite(value) || value <= 0) return null;
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1);
};

const normalizeWorkouts = (rawWorkouts) => {
  if (!Array.isArray(rawWorkouts)) return [];
  return rawWorkouts.map((workout, workoutIndex) => {
    const safeWorkout =
      workout && typeof workout === "object" ? workout : {};
    return {
      id: safeWorkout.id || createId(`wk-${workoutIndex}`),
      sessionsPerWeek: safeWorkout.sessionsPerWeek ?? "",
      durationMinutes: safeWorkout.durationMinutes ?? "",
      effort: safeWorkout.effort ?? "",
      exercises: Array.isArray(safeWorkout.exercises)
        ? safeWorkout.exercises.map((exercise, exerciseIndex) => ({
            id:
              exercise.id ||
              createId(`ex-${workoutIndex}-${exerciseIndex}`),
            exerciseId: exercise.exerciseId || "",
            sets: exercise.sets ?? "",
            reps: exercise.reps ?? "",
            weight: exercise.weight ?? "",
            isOpen: exercise.isOpen ?? true,
          }))
        : [],
      isOpen: safeWorkout.isOpen ?? true,
    };
  });
};

const loadStoredState = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const hasStrengthWorkoutDetail = (workout) => {
  const hasExercises = (workout?.exercises || []).some(
    (exercise) => !!exercise.exerciseId
  );
  const sessions = parseNumber(workout?.sessionsPerWeek);
  return hasExercises && Number.isFinite(sessions) && sessions > 0;
};

const hasCardioDetails = (session) => {
  if (!session || typeof session !== "object") return false;
  const sessions = parseNumber(session.sessionsPerWeek);
  return Number.isFinite(sessions) && sessions > 0;
};

export default function Calculator() {
  const accuracyRef = useRef(null);
  const calculatorSectionRef = useRef(null);
  const calculatorEndRef = useRef(null);
  const [basics, setBasics] = useState(defaultBasics);
  const [lifestyle, setLifestyle] = useState(defaultLifestyle);

  const [strengthWorkouts, setStrengthWorkouts] = useState([]);
  const [strengthUnit, setStrengthUnit] = useState("kg");
  const [cardioSessions, setCardioSessions] = useState([]);
  const [sportsSessions, setSportsSessions] = useState([]);
  const [useWorkoutProfile, setUseWorkoutProfile] = useState(false);
  const [showResultsOnly, setShowResultsOnly] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [showAccuracyBar, setShowAccuracyBar] = useState(false);
  const [bootDone, setBootDone] = useState(false);
  const [showAccuracyModal, setShowAccuracyModal] = useState(false);
  const [bannerAccuracy, setBannerAccuracy] = useState(100);
  const [showDesktopResultsPanel, setShowDesktopResultsPanel] =
    useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const hasAnimatedBannerRef = useRef(false);
  const lastPanelToggleRef = useRef(0);

  useEffect(() => {
    const stored = loadStoredState();
    if (stored) {
      if (stored.basics && typeof stored.basics === "object") {
        setBasics({ ...defaultBasics, ...stored.basics });
      }
      if (stored.lifestyle && typeof stored.lifestyle === "object") {
        setLifestyle({ ...defaultLifestyle, ...stored.lifestyle });
      }
      const normalizedWorkouts = normalizeWorkouts(
        stored.strengthWorkouts
      );
      if (normalizedWorkouts.length > 0) {
        setStrengthWorkouts(normalizedWorkouts);
      } else if (Array.isArray(stored.strengthExercises)) {
        const hasExercises = stored.strengthExercises.length > 0;
        const hasSchedule =
          stored.strengthWorkout &&
          (stored.strengthWorkout.sessionsPerWeek ||
            stored.strengthWorkout.durationMinutes ||
            stored.strengthWorkout.effort);
        if (hasExercises || hasSchedule) {
          const migratedWorkout = {
            id: createId("wk-migrated"),
            sessionsPerWeek:
              stored.strengthWorkout?.sessionsPerWeek ?? "",
            durationMinutes:
              stored.strengthWorkout?.durationMinutes ?? "",
            effort: stored.strengthWorkout?.effort ?? "",
            exercises: stored.strengthExercises,
            isOpen: true,
          };
          setStrengthWorkouts(normalizeWorkouts([migratedWorkout]));
        }
      }
      if (stored.strengthUnit === "kg" || stored.strengthUnit === "lb") {
        setStrengthUnit(stored.strengthUnit);
      }
      if (Array.isArray(stored.cardioSessions)) {
        setCardioSessions(stored.cardioSessions);
      }
      if (Array.isArray(stored.sportsSessions)) {
        setSportsSessions(stored.sportsSessions);
      }
      if (typeof stored.useWorkoutProfile === "boolean") {
        setUseWorkoutProfile(stored.useWorkoutProfile);
      }
    }
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated || typeof window === "undefined") return;
    const payload = {
      basics,
      lifestyle,
      strengthWorkouts,
      strengthUnit,
      cardioSessions,
      sportsSessions,
      useWorkoutProfile,
    };
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(payload)
    );
  }, [
    isHydrated,
    basics,
    lifestyle,
    strengthWorkouts,
    strengthUnit,
    cardioSessions,
    sportsSessions,
    useWorkoutProfile,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (showResultsOnly) {
      setShowAccuracyBar(false);
      return;
    }
    const target = accuracyRef.current;
    if (!target) return;
    const media = window.matchMedia("(max-width: 768px)");
    const updateVisibility = (entry) => {
      if (media.matches) {
        setShowAccuracyBar(true);
        return;
      }
      if (!entry) return;
      setShowAccuracyBar(!entry.isIntersecting);
    };
    const observer = new IntersectionObserver(
      (entries) => {
        updateVisibility(entries[0]);
      },
      { threshold: 0.2, rootMargin: "-12px 0px 0px 0px" }
    );
    observer.observe(target);
    const handleMedia = () => updateVisibility({ isIntersecting: true });
    media.addEventListener("change", handleMedia);
    updateVisibility({ isIntersecting: true });
    return () => {
      observer.disconnect();
      media.removeEventListener("change", handleMedia);
    };
  }, [showResultsOnly]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia("(min-width: 1024px)");
    const setFromMedia = () => setIsDesktop(media.matches);
    setFromMedia();
    media.addEventListener("change", setFromMedia);
    return () => media.removeEventListener("change", setFromMedia);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isDesktop || showResultsOnly) {
      setShowDesktopResultsPanel(false);
      return;
    }
    const target = accuracyRef.current;
    const section = calculatorSectionRef.current;
    const sectionEnd = calculatorEndRef.current;
    if (!target || !section || !sectionEnd) return;
    let rafId = null;
    const openAt = -40;
    const closeAt = 220;
    const update = () => {
      rafId = null;
      const rect = target.getBoundingClientRect();
      const bottom = rect.bottom;
      const sectionRect = section.getBoundingClientRect();
      const endRect = sectionEnd.getBoundingClientRect();
      const sectionActive =
        sectionRect.bottom > 140 && sectionRect.top < window.innerHeight;
      const withinCalculator = endRect.top > 80;
      setShowDesktopResultsPanel((prev) => {
        const now = performance.now();
          if (now - lastPanelToggleRef.current < 700) {
            return prev;
          }
        let next = prev;
        if (!sectionActive || !withinCalculator) {
          next = false;
        } else {
          if (!prev && bottom <= openAt) next = true;
          if (prev && bottom >= closeAt) next = false;
        }
        if (next !== prev) {
          lastPanelToggleRef.current = now;
        }
        return next;
      });
    };
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [isDesktop, showResultsOnly]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const shouldOpen =
      isDesktop && showDesktopResultsPanel && !showResultsOnly;
    document.body.classList.toggle(
      "desktop-results-open",
      shouldOpen
    );
    document.documentElement.classList.toggle(
      "desktop-results-open",
      shouldOpen
    );
    return () => {
      document.body.classList.remove("desktop-results-open");
      document.documentElement.classList.remove(
        "desktop-results-open"
      );
    };
  }, [isDesktop, showDesktopResultsPanel, showResultsOnly]);


  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const bootLoader = document.getElementById("boot-loader");
    const bootCompleted =
      !bootLoader ||
      bootLoader.classList.contains("is-hidden") ||
      !document.body.classList.contains("boot-loading");
    if (bootCompleted) {
      setBootDone(true);
    }
    const handleBootDone = () => setBootDone(true);
    window.addEventListener("bootloader:done", handleBootDone);
    return () => {
      window.removeEventListener("bootloader:done", handleBootDone);
    };
  }, []);

  useEffect(() => {
    if (!showResultsOnly || typeof window === "undefined") return;
    const target = document.getElementById("results-view");
    if (target) {
      target.scrollIntoView({ behavior: "smooth" });
    }
  }, [showResultsOnly]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.classList.toggle("results-only", showResultsOnly);
    document.documentElement.classList.toggle(
      "results-only",
      showResultsOnly
    );
    return () => {
      document.body.classList.remove("results-only");
      document.documentElement.classList.remove("results-only");
    };
  }, [showResultsOnly]);

  const weightKgForWorkouts = useMemo(
    () => convertWeightToKg(basics.weight, basics.weightUnit),
    [basics.weight, basics.weightUnit]
  );

  const derivedTraining = useMemo(
    () =>
      deriveTrainingProfile({
        strengthWorkouts,
        cardioSessions,
        strengthUnit,
        weightKg: weightKgForWorkouts,
      }),
    [strengthWorkouts, cardioSessions, strengthUnit, weightKgForWorkouts]
  );

  const effectiveTraining = useMemo(() => {
    if (useWorkoutProfile) {
      return derivedTraining;
    }
    return {
      trainingFrequency: lifestyle.trainingFrequency,
      trainingIntensity: lifestyle.trainingIntensity,
      trainingModality: lifestyle.trainingModality,
    };
  }, [
    useWorkoutProfile,
    derivedTraining,
    lifestyle.trainingFrequency,
    lifestyle.trainingIntensity,
    lifestyle.trainingModality,
  ]);

  const lifestyleForCalculations = useMemo(
    () => ({
      ...lifestyle,
      ...effectiveTraining,
    }),
    [lifestyle, effectiveTraining]
  );

  const handleHeightUnitChange = (unit) => {
    setBasics((prev) => {
      if (prev.heightUnit === unit) return prev;
      if (unit === "cm") {
        const cmValue = convertHeightToCm({
          unit: prev.heightUnit,
          heightCm: prev.heightCm,
          heightFt: prev.heightFt,
          heightIn: prev.heightIn,
        });
        return {
          ...prev,
          heightUnit: "cm",
          heightCm: cmValue ? Math.round(cmValue) : "",
        };
      }
      const { feet, inches } = convertCmToFeetInches(
        parseNumber(prev.heightCm)
      );
      return {
        ...prev,
        heightUnit: "ft",
        heightFt: feet,
        heightIn: inches,
      };
    });
  };

  const handleWeightUnitChange = (unit) => {
    setBasics((prev) => {
      if (prev.weightUnit === unit) return prev;
      const weightKg = convertWeightToKg(
        prev.weight,
        prev.weightUnit
      );
      const converted = weightKg
        ? convertKgToUnit(weightKg, unit)
        : null;
      return {
        ...prev,
        weightUnit: unit,
        weight: converted ? converted.toFixed(1) : "",
      };
    });
  };

  const handleStrengthUnitChange = (unit) => {
    if (unit === strengthUnit) return;
    setStrengthWorkouts((prev) =>
      prev.map((workout) => ({
        ...workout,
        exercises: (workout.exercises || []).map((exercise) => {
          const weightKg = convertWeightToKg(
            exercise.weight,
            strengthUnit
          );
          const converted = weightKg
            ? convertKgToUnit(weightKg, unit)
            : null;
          return {
            ...exercise,
            weight: converted ? converted.toFixed(1) : "",
          };
        }),
      }))
    );
    setStrengthUnit(unit);
  };

  const validation = useMemo(() => {
    const ageValue = parseNumber(basics.age);
    const heightCm = convertHeightToCm({
      unit: basics.heightUnit,
      heightCm: basics.heightCm,
      heightFt: basics.heightFt,
      heightIn: basics.heightIn,
    });
    const weightKg = convertWeightToKg(
      basics.weight,
      basics.weightUnit
    );
    const bodyFatPct = parseNumber(basics.bodyFatPct);

    const ageReady =
      Number.isFinite(ageValue) &&
      ageValue >= ageRange.min &&
      ageValue <= ageRange.max;
    const heightReady =
      Number.isFinite(heightCm) &&
      heightCm >= heightRange.min &&
      heightCm <= heightRange.max;
    const weightReady =
      Number.isFinite(weightKg) &&
      weightKg >= weightRange.min &&
      weightKg <= weightRange.max;
    const bodyFatReady =
      Number.isFinite(bodyFatPct) &&
      bodyFatPct >= bodyFatRange.min &&
      bodyFatPct <= bodyFatRange.max;

    return {
      ageValid: !basics.age || ageReady,
      heightValid:
        (!basics.heightCm &&
          !basics.heightFt &&
          !basics.heightIn) ||
        heightReady,
      weightValid: !basics.weight || weightReady,
      bodyFatValid: !basics.bodyFatPct || bodyFatReady,
      basicsValid:
        !!basics.sex && ageReady && heightReady && weightReady,
      heightCm,
      weightKg,
      ageValue,
      bodyFatPct: bodyFatReady ? bodyFatPct : null,
    };
  }, [basics]);

  const results = useMemo(() => {
    const leanMassKg = calculateLeanMassKg({
      weightKg: validation.weightKg,
      bodyFatPct: validation.bodyFatPct,
    });
    const bmrValue = validation.basicsValid
      ? calculateBmr({
          sex: basics.sex,
          age: validation.ageValue,
          heightCm: validation.heightCm,
          weightKg: validation.weightKg,
          bodyFatPct: validation.bodyFatPct,
        })
      : null;

    const { baseActivity, assumed } =
      getBaseActivityMultiplier(lifestyleForCalculations.jobActivity);
    const stepsAdjustment = calculateStepsAdjustment({
      steps: lifestyleForCalculations.dailySteps,
      weightKg: validation.weightKg,
      jobActivity: lifestyleForCalculations.jobActivity,
    });
    const workoutCaloriesPerDay = useWorkoutProfile
      ? derivedTraining.workoutCaloriesPerDay
      : 0;
    const trainingLoadAdd =
      bmrValue && workoutCaloriesPerDay
        ? workoutCaloriesPerDay / bmrValue
        : 0;
    const stepsLoadAdd =
      bmrValue && stepsAdjustment.stepsCaloriesPerDay
        ? stepsAdjustment.stepsCaloriesPerDay / bmrValue
        : 0;
    const modifiers = getTrainingModifiers({
      ...lifestyleForCalculations,
      trainingLoadAdd,
      stepsLoadAdd,
      useWorkoutCalories: useWorkoutProfile,
    });
    const tdeeData = calculateTdee({
      bmr: bmrValue,
      baseActivity,
      modifiers,
      maxMultiplier: useWorkoutProfile ? 3.0 : 2.4,
    });

    const goalAdjustments = {
      fat_loss: -300,
      performance: 150,
      recomp: -100,
    };

    const recommendedTarget =
      tdeeData && lifestyle.goal
        ? tdeeData.tdee + goalAdjustments[lifestyle.goal]
        : null;

    const calculatorNet = getCalculatorNetActivity({
      trainingFrequency: lifestyleForCalculations.trainingFrequency,
      jobActivity: lifestyleForCalculations.jobActivity,
    });
    const baselineTdee = bmrValue
      ? bmrValue * calculatorNet.multiplier
      : null;
    const delta =
      tdeeData && baselineTdee
        ? tdeeData.tdee - baselineTdee
        : null;
    const leanMassLb = Number.isFinite(leanMassKg)
      ? convertKgToUnit(leanMassKg, "lb")
      : null;

    return {
      bmr: bmrValue,
      tdee: tdeeData ? tdeeData.tdee : null,
      recommendedTarget,
      protein: Number.isFinite(leanMassLb)
        ? Math.round(leanMassLb * 1.2)
        : null,
      leanMassKg,
      leanMassLb,
      bodyFatPct: validation.bodyFatPct,
      bmrMethod: Number.isFinite(leanMassKg)
        ? "lean"
        : "mifflin",
      baseActivity,
      assumedActivity: assumed,
      modifiers,
      totalMultiplier: tdeeData ? tdeeData.totalMultiplier : 0,
      rawMultiplier: tdeeData ? tdeeData.rawMultiplier : 0,
      wasClamped: tdeeData ? tdeeData.wasClamped : false,
      goal: lifestyleForCalculations.goal,
      baselineTdee,
      delta,
      calculatorNetMultiplier: calculatorNet.multiplier,
      calculatorNetLabel: calculatorNet.label,
      stepsCaloriesPerDay: stepsAdjustment.stepsCaloriesPerDay,
      stepsBaseline: stepsAdjustment.baselineSteps,
      strengthCaloriesPerWeek:
        derivedTraining.strengthCaloriesPerWeek || 0,
      cardioCaloriesPerWeek:
        derivedTraining.cardioCaloriesPerWeek || 0,
      workoutCaloriesPerWeek:
        derivedTraining.workoutCaloriesPerWeek || 0,
      workoutCaloriesPerDay,
      workoutSessionsPerWeek:
        derivedTraining.workoutSessionsPerWeek || 0,
      strengthSessionsPerWeek: derivedTraining.strengthSessions || 0,
      cardioSessionsPerWeek: derivedTraining.cardioSessions || 0,
    };
  }, [
    basics,
    lifestyleForCalculations,
    validation,
    useWorkoutProfile,
    derivedTraining.strengthCaloriesPerWeek,
    derivedTraining.cardioCaloriesPerWeek,
    derivedTraining.workoutCaloriesPerWeek,
    derivedTraining.workoutCaloriesPerDay,
    derivedTraining.workoutSessionsPerWeek,
    derivedTraining.strengthSessions,
    derivedTraining.cardioSessions,
  ]);

  const strengthWorkoutCount = useMemo(
    () =>
      strengthWorkouts.reduce(
        (total, workout) =>
          total + (hasStrengthWorkoutDetail(workout) ? 1 : 0),
        0
      ),
    [strengthWorkouts]
  );

  const cardioEntryCount = useMemo(
    () => cardioSessions.filter(hasCardioDetails).length,
    [cardioSessions]
  );

  const accuracy = useMemo(
    () =>
      calculateConfidence({
        basicsValid: validation.basicsValid,
        lifestyle: lifestyleForCalculations,
        strengthWorkouts,
        cardioSessions,
        bodyFatPct: validation.bodyFatPct,
        trainingSource: useWorkoutProfile ? "workout" : "manual",
      }),
    [
      validation.basicsValid,
      lifestyleForCalculations,
      strengthWorkouts,
      cardioSessions,
      validation.bodyFatPct,
      useWorkoutProfile,
    ]
  );

  const trainingSummary = useMemo(() => {
    const intensityLabel =
      trainingLabels[effectiveTraining.trainingIntensity] ||
      "--";
    const modalityLabel =
      trainingLabels[effectiveTraining.trainingModality] || "--";
    const loadNote = useWorkoutProfile
      ? derivedTraining.workoutCaloriesPerDay > 0
        ? ` (workouts +${Math.round(
            derivedTraining.workoutCaloriesPerDay
          )} kcal/day)`
        : " (add weight or minutes to increase workout calories)"
      : "";
    if (useWorkoutProfile) {
      if (derivedTraining.workoutSessionsPerWeek === 0) {
        return "Add weekly sessions to derive training days.";
      }
      const strengthSessions = formatSessionCount(
        derivedTraining.strengthSessions
      );
      const cardioSessions = formatSessionCount(
        derivedTraining.cardioSessions
      );
      let sessionLabel = "";
      if (strengthSessions && cardioSessions) {
        sessionLabel = `${strengthSessions} strength + ${cardioSessions} cardio sessions/week`;
      } else if (strengthSessions) {
        sessionLabel = `${strengthSessions} strength sessions/week`;
      } else if (cardioSessions) {
        sessionLabel = `${cardioSessions} cardio sessions/week`;
      } else {
        sessionLabel = "Add weekly sessions to derive training days.";
      }
      return `${sessionLabel} - ${intensityLabel} - ${modalityLabel}${loadNote}`;
    }

    const freqValue = parseNumber(
      effectiveTraining.trainingFrequency
    );
    if (!Number.isFinite(freqValue)) {
      return "Select a training frequency.";
    }
    return `${freqValue} days/week - ${intensityLabel} - ${modalityLabel}${loadNote}`;
  }, [
    effectiveTraining,
    useWorkoutProfile,
    derivedTraining.workoutSessionsPerWeek,
    derivedTraining.strengthSessions,
    derivedTraining.cardioSessions,
    derivedTraining.workoutCaloriesPerDay,
  ]);

  const showResultsButton = !showResultsOnly && validation.basicsValid;
  const targetAccuracy = Math.round(
    Math.max(0, Math.min(95, accuracy.score))
  );
  const accuracyDisplay = bootDone ? targetAccuracy : 100;

  useEffect(() => {
    if (!bootDone) {
      hasAnimatedBannerRef.current = false;
      setBannerAccuracy(100);
      return;
    }

    if (!hasAnimatedBannerRef.current) {
      const start = 100;
      const end = targetAccuracy;
      const duration = 2800;
      const startTime = performance.now();
      let rafId;

      const tick = (time) => {
        const progress = Math.min(1, (time - startTime) / duration);
        const nextValue = start + (end - start) * progress;
        setBannerAccuracy(nextValue);
        if (progress < 1) {
          rafId = requestAnimationFrame(tick);
        } else {
          hasAnimatedBannerRef.current = true;
        }
      };

      rafId = requestAnimationFrame(tick);
      return () => {
        if (rafId) cancelAnimationFrame(rafId);
      };
    }

    setBannerAccuracy(targetAccuracy);
  }, [bootDone, targetAccuracy]);

  return (
    <div className="calculator-page min-h-screen bg-[var(--bg)] text-[var(--ink)]">
      {showResultsOnly ? null : (
        <div
          className={`accuracy-bar ${
            showAccuracyBar ? "is-visible" : ""
          }`}
        >
          <div className="accuracy-bar__inner">
            <div className="accuracy-bar__copy">
              <p className="accuracy-bar__label">
                Accuracy estimation
              </p>
              <p className="accuracy-bar__note">
                {Math.round(bannerAccuracy)}% confidence
              </p>
            </div>
            <div className="accuracy-bar__actions">
              {showResultsButton ? (
                <button
                  type="button"
                  onClick={() => setShowResultsOnly(true)}
                  className="accuracy-bar__results"
                >
                  Show results
                </button>
              ) : null}
              <button
                type="button"
                className="accuracy-bar__help"
                onClick={() => setShowAccuracyModal(true)}
                aria-label="What is accuracy?"
              >
                ?
              </button>
            </div>
          </div>
          <div className="accuracy-bar__track">
            <div
              className="accuracy-bar__fill"
              style={{
                width: `${Math.min(100, bannerAccuracy)}%`,
              }}
            />
          </div>
        </div>
      )}
      {showAccuracyModal ? (
        <div
          className="accuracy-modal"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="accuracy-modal__backdrop"
            onClick={() => setShowAccuracyModal(false)}
            aria-label="Close accuracy details"
          />
          <div className="accuracy-modal__card">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
                Accuracy estimation
              </p>
              <button
                type="button"
                className="accuracy-modal__close"
                onClick={() => setShowAccuracyModal(false)}
              >
                Close
              </button>
            </div>
            <div className="mt-4">
              <div className="accuracy-modal__value">
                {accuracyDisplay}%
              </div>
              <div className="accuracy-modal__track">
                <div
                  className="accuracy-modal__fill"
                  style={{
                    width: `${Math.min(100, accuracyDisplay)}%`,
                  }}
                />
              </div>
              <p className="mt-3 text-sm text-[var(--muted)]">
                More detail increases confidence. We split training
                frequency, intensity, and modality instead of using
                one generic activity multiplier.
              </p>
              <ul className="mt-3 space-y-2 text-xs text-[var(--muted)]">
                <li>Log weekly strength workouts and cardio sessions.</li>
                <li>Add steps and job activity for daily movement.</li>
                <li>Include sleep quality and diet history.</li>
                <li>Enter body fat % for a lean-mass BMR estimate.</li>
              </ul>
            </div>
          </div>
        </div>
      ) : null}
      {showResultsOnly ? null : (
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 hero-gradient" />
          <div
            className="absolute -top-24 left-10 h-60 w-60 rounded-full opacity-40 blur-3xl animate-float"
            style={{ backgroundColor: "var(--accent-3)" }}
          />
          <div
            className="absolute top-10 right-10 h-72 w-72 rounded-full opacity-25 blur-3xl animate-float-slow"
            style={{ backgroundColor: "var(--accent-2)" }}
          />
          <div className="hero-container relative mx-auto max-w-6xl px-4 pb-16 pt-14">
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[1.1fr_0.9fr]">
              <div className="space-y-6">
                <div className="hero-pill inline-flex items-center gap-3 rounded-full bg-white/70 px-3 py-1 text-xs uppercase tracking-[0.2em] text-[var(--muted)]">
                  <FireLogo size={54} data-hero-logo />
                  <span>BetterBMRCalculator</span>
                </div>
                <h1 className="hero-title text-4xl font-display leading-tight text-[var(--ink)] md:text-5xl">
                  A BMR and TDEE calculator that shows how accurate it is
                  in real time.
                </h1>
                <p className="hero-subtitle max-w-xl text-base text-[var(--muted)]">
                  Most calculators stop at a generic activity multiplier.
                  Here, your weekly details lift the Accuracy Estimation
                  so you can see why the number is stronger.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <a
                    href="#calculator"
                    className="inline-flex items-center justify-center rounded-full bg-[var(--accent)] px-5 py-2 text-sm font-semibold text-white shadow-[0_18px_40px_rgba(227,108,79,0.35)] transition hover:-translate-y-0.5"
                  >
                    Start the calculator
                  </a>
                  <Link
                    to="/donate"
                    className="inline-flex items-center justify-center rounded-full border border-white/70 bg-white/80 px-5 py-2 text-sm font-semibold text-[var(--ink)] transition hover:-translate-y-0.5 hover:shadow-lg"
                  >
                    Support the project
                  </Link>
                  <Link
                    to="/goals"
                    className="inline-flex items-center justify-center rounded-full border border-white/70 bg-white/80 px-5 py-2 text-sm font-semibold text-[var(--ink)] transition hover:-translate-y-0.5 hover:shadow-lg"
                  >
                    Project goals
                  </Link>
                  <span className="text-xs uppercase tracking-[0.2em] text-[var(--muted)]">
                    Detailed, deterministic estimates
                  </span>
                </div>
              </div>
              <div ref={accuracyRef} className="accuracy-gauge">
                <AccuracyGauge
                  value={accuracy.score}
                  checklist={accuracy.checklist}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <section
        id="calculator"
        ref={calculatorSectionRef}
        className={`mx-auto max-w-7xl px-4 ${
          showResultsOnly ? "py-4" : "pb-20"
        }`}
      >
        {showResultsOnly ? (
          <div id="results-view" className="space-y-2 results-only-view">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="inline-flex items-center gap-3 rounded-full bg-white/70 px-3 py-1 text-xs uppercase tracking-[0.2em] text-[var(--muted)]">
                  <FireLogo size={40} />
                  <span>BetterBMRCalculator</span>
                </div>
                <h2 className="mt-2 text-xl font-display text-[var(--ink)]">
                  Results overview
                </h2>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to="/donate"
                  className="inline-flex items-center justify-center rounded-full bg-[var(--accent)] px-5 py-2 text-sm font-semibold text-white shadow-[0_18px_40px_rgba(227,108,79,0.35)] transition hover:-translate-y-0.5"
                >
                  Support the project
                </Link>
                <Link
                  to="/goals"
                  className="inline-flex items-center justify-center rounded-full border border-white/70 bg-white/80 px-5 py-2 text-sm font-semibold text-[var(--ink)] transition hover:-translate-y-0.5 hover:shadow-lg"
                >
                  Project goals
                </Link>
                <button
                  type="button"
                  onClick={() => setShowResultsOnly(false)}
                  className="inline-flex items-center justify-center rounded-full border border-white/70 bg-white/80 px-5 py-2 text-sm font-semibold text-[var(--ink)] transition hover:-translate-y-0.5 hover:shadow-lg"
                >
                  Edit inputs
                </button>
              </div>
            </div>
            <ResultsPanel
              results={results}
              basicsValid={validation.basicsValid}
              strengthCount={strengthWorkoutCount}
              cardioCount={cardioEntryCount}
              accuracyScore={accuracy.score}
              trainingSource={useWorkoutProfile ? "workout" : "manual"}
              trainingSummary={trainingSummary}
              layout="resultsOnly"
            />
            <p className="text-[10px] text-[var(--muted)]">
              Estimates are for informational purposes only and do not
              replace professional medical advice.
            </p>
            <Footer />
          </div>
        ) : (
          <>
            <div
              className={`results-grid results-layout grid grid-cols-1 gap-8 lg:grid-cols-[1.2fr_0.8fr] ${
                isDesktop ? "results-layout--desktop" : ""
              } ${showDesktopResultsPanel ? "is-expanded" : ""}`}
            >
              <div className="results-layout__main">
                <CalculatorForm
                  basics={basics}
                  setBasics={setBasics}
                  lifestyle={lifestyle}
                  setLifestyle={setLifestyle}
                  useWorkoutProfile={useWorkoutProfile}
                  setUseWorkoutProfile={setUseWorkoutProfile}
                  derivedTraining={derivedTraining}
                  strengthWorkouts={strengthWorkouts}
                  setStrengthWorkouts={setStrengthWorkouts}
                  strengthUnit={strengthUnit}
                  onStrengthUnitChange={handleStrengthUnitChange}
                  cardioSessions={cardioSessions}
                  setCardioSessions={setCardioSessions}
                  sportsSessions={sportsSessions}
                  setSportsSessions={setSportsSessions}
                  validation={validation}
                  onHeightUnitChange={handleHeightUnitChange}
                  onWeightUnitChange={handleWeightUnitChange}
                />
                <div ref={calculatorEndRef} aria-hidden="true" />
              </div>
              {isDesktop ? (
                <aside
                  className="results-layout__panel"
                  aria-hidden={!showDesktopResultsPanel}
                >
                  <div className="results-layout__panel-inner">
                    <ResultsPanel
                      results={results}
                      basicsValid={validation.basicsValid}
                      strengthCount={strengthWorkoutCount}
                      cardioCount={cardioEntryCount}
                      accuracyScore={accuracy.score}
                      trainingSource={
                        useWorkoutProfile ? "workout" : "manual"
                      }
                      trainingSummary={trainingSummary}
                      compact
                    />
                  </div>
                </aside>
              ) : (
                <div className="results-sidebar">
                  <div className="results-sidebar__inner">
                    <ResultsPanel
                      results={results}
                      basicsValid={validation.basicsValid}
                      strengthCount={strengthWorkoutCount}
                      cardioCount={cardioEntryCount}
                      accuracyScore={accuracy.score}
                      trainingSource={
                        useWorkoutProfile ? "workout" : "manual"
                      }
                      trainingSummary={trainingSummary}
                      compact
                    />
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </section>

      {showResultsButton ? (
        <button
          type="button"
          onClick={() => setShowResultsOnly(true)}
          className="show-results-fab fixed bottom-6 right-6 z-50 inline-flex items-center justify-center rounded-full bg-[var(--accent)] px-6 py-3 text-sm font-semibold text-white shadow-[0_18px_40px_rgba(227,108,79,0.35)] transition hover:-translate-y-0.5"
        >
          Show results
        </button>
      ) : null}

      {showResultsOnly ? null : (
        <>
          <section className="mx-auto max-w-6xl px-4 pb-16">
            <div className="glass-card p-6 md:p-8">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
                    Why accuracy is higher
                  </p>
                  <h2 className="mt-2 text-2xl font-display text-[var(--ink)]">
                    More detail = less guesswork.
                  </h2>
                </div>
              </div>
              <ul className="mt-5 grid list-disc gap-3 pl-5 text-sm text-[var(--muted)] md:grid-cols-2">
                <li>
                  Typical calculators treat exercise as a single multiplier.
                </li>
                <li>
                  Training frequency, intensity, and modality each shift
                  metabolic demand. We reflect that.
                </li>
                <li>
                  Optional weekly strength and cardio logs add
                  higher-resolution activity detail for confidence.
                </li>
                <li>
                  Sleep quality and diet history reduce the noise in
                  recovery and energy balance.
                </li>
              </ul>
            </div>
          </section>

          <section className="mx-auto max-w-6xl px-4 pb-16">
            <div className="glass-card p-6 md:p-8">
              <p className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
                BMR & TDEE FAQ
              </p>
              <h2 className="mt-2 text-2xl font-display text-[var(--ink)]">
                Better answers to common BMR and TDEE questions.
              </h2>
              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-white/70 bg-white/70 p-4">
                  <h3 className="text-sm font-semibold text-[var(--ink)]">
                    What is BMR?
                  </h3>
                  <p className="mt-2 text-sm text-[var(--muted)]">
                    Basal Metabolic Rate (BMR) is the energy your body
                    uses at rest to support essential functions like
                    breathing and circulation.
                  </p>
                </div>
                <div className="rounded-2xl border border-white/70 bg-white/70 p-4">
                  <h3 className="text-sm font-semibold text-[var(--ink)]">
                    What is TDEE?
                  </h3>
                  <p className="mt-2 text-sm text-[var(--muted)]">
                    Total Daily Energy Expenditure (TDEE) estimates
                    your daily calories after adding activity,
                    training, and lifestyle context to your BMR.
                  </p>
                </div>
                <div className="rounded-2xl border border-white/70 bg-white/70 p-4">
                  <h3 className="text-sm font-semibold text-[var(--ink)]">
                    Why does accuracy change here?
                  </h3>
                  <p className="mt-2 text-sm text-[var(--muted)]">
                    The Accuracy Estimation increases when you include
                    steps, sleep, diet history, and detailed weekly
                    workouts instead of a generic multiplier.
                  </p>
                </div>
                <div className="rounded-2xl border border-white/70 bg-white/70 p-4">
                  <h3 className="text-sm font-semibold text-[var(--ink)]">
                    Is this a medical diagnosis?
                  </h3>
                  <p className="mt-2 text-sm text-[var(--muted)]">
                    No. This calculator is for informational use and
                    should not replace guidance from a qualified
                    healthcare professional.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="mx-auto max-w-6xl px-4 pb-16">
            <div className="glass-card p-6 md:p-8">
              <p className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
                Privacy & Cookies
              </p>
              <h2 className="mt-2 text-2xl font-display text-[var(--ink)]">
                Your data stays on your device.
              </h2>
              <ul className="mt-4 grid gap-3 text-sm text-[var(--muted)] md:grid-cols-2">
                <li>
                  Calculator inputs are stored locally in your browser so
                  you don&apos;t have to retype them.
                </li>
                <li>
                  We do not ask for accounts or store your inputs on a
                  server.
                </li>
                <li>
                  We don&apos;t run ads or third-party tracking
                  scripts.
                </li>
              </ul>
            </div>
          </section>

          <section className="mx-auto max-w-6xl px-4 pb-16">
            <ComingSoonSection />
          </section>
        </>
      )}

      {showResultsOnly ? null : (
        <div className={showResultsButton ? "pb-14 md:pb-0" : ""}>
          <Footer />
        </div>
      )}
    </div>
  );
}
